/**
 * Home: where the console opens. For a new organisation it is a setup checklist, each step a card
 * that is done or not and goes to the place that does it; once every step is done (or for someone who
 * is not an administrator), it is the workbench's Home: the week ahead, the numbers and the feed of what
 * needs you, what is running and what finished across the organisation (components/home/HomeView.tsx).
 */

import { CircleCheck, Database, Display, Globe, House, Key, Pulse, Shield, SquarePlus, Bell, CodeTrunk } from '@gravity-ui/icons';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useCloudStanding } from '../cloudPlan.ts';
import { isCloud } from '../edition.ts';
import { CloudAccountHome } from './CloudAccountHome.tsx';
import { Page, PageHeader, SectionHeading, Stat } from '../components/ui.tsx';
import { ArtSuggestion, VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad } from '../hooks.ts';
import { AdoptionPanel } from '../components/AdoptionPanel.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { LiveSessionsSection } from '../components/live/LiveSessions.tsx';
import { HomeClock, HomeView } from '../components/home/HomeView.tsx';

const tone = VIEW_TONES.home!;

interface Step {
  key: string;
  title: string;
  /** What it does, in a sentence. */
  why: string;
  icon: ReactNode;
  to: string;
  /** Undefined while not known (the API refused or has not answered). */
  done?: boolean;
}

/** Cloud without Teams is an account, so its Home is the account's (CloudAccountHome); everywhere else, the organisation's. */
export function HomePage() {
  const { cloud, teams } = useCloudStanding();
  return cloud && !teams ? <CloudAccountHome /> : <OrganisationHome />;
}

function OrganisationHome() {
  const navigate = useNavigate();
  const { discovery } = useAuth();
  const cloud = isCloud();
  const devices = useLoad(() => routes.devices().catch(() => undefined), []);
  const policies = useLoad(() => routes.policies().catch(() => undefined), []);
  const sources = useLoad(() => routes.sourceConnections().catch(() => undefined), []);
  const licence = useLoad(() => (cloud ? Promise.resolve(undefined) : routes.licence().catch(() => undefined)), []);
  const clearances = useLoad(() => (cloud ? Promise.resolve(undefined) : routes.clearances().catch(() => undefined)), []);
  const live = useLoad(() => routes.live().catch(() => undefined), [], 30_000);
  const inbox = useLoad(() => routes.inbox('unread', 50).catch(() => undefined), [], 60_000);
  const pending = useLoad(() => routes.pendingNodes().catch(() => undefined), [], 60_000);
  // D282: the person's own sessions open to remote control; the section is hidden when there are none.
  const myLive = useLoad(() => routes.myLive().catch(() => undefined), [], 30_000);

  const enrolled = devices.data ? devices.data.length > 0 : undefined;
  const steps: Step[] = [
    ...(cloud
      ? []
      : [
          {
            key: 'discovery',
            title: 'Let workbenches find this service',
            why: 'Publish one DNS record for your email domain. Done once a workbench has found it.',
            icon: <Globe />,
            to: '/settings/platform',
            done: enrolled,
          },
          {
            key: 'licence',
            title: 'Install the licence',
            why: 'It says how many people and nodes this organisation may run.',
            icon: <Key />,
            to: '/settings/organisation',
            done: licence.data ? ['licensed', 'expiring'].includes(licence.data.state) : undefined,
          },
        ]),
    {
      key: 'source',
      title: 'Connect source control',
      why: 'GitHub or Azure DevOps, so projects find their repositories.',
      icon: <CodeTrunk />,
      to: '/settings/integrations',
      done: sources.data ? sources.data.length > 0 : undefined,
    },
    {
      key: 'enrol',
      title: 'Add the first workbench',
      why: 'Approve the code AI Workbench shows when it connects.',
      icon: <SquarePlus />,
      to: '/enrol',
      done: enrolled,
    },
    {
      key: 'policy',
      title: 'Publish the organisation default policy',
      why: 'The rules every project follows until it has its own.',
      icon: <Shield />,
      to: policies.data?.some((p) => p.projectIds.length === 0) ? '/policies' : '/policies/new',
      done: policies.data ? policies.data.some((p) => p.projectIds.length === 0) : undefined,
    },
    ...(cloud
      ? []
      : [
          {
            key: 'memory',
            title: 'Decide who reads Confidential memory',
            why: 'Until someone is cleared, people read only what is labelled Internal or lower.',
            icon: <Database />,
            to: '/memory',
            done: clearances.data ? clearances.data.length > 0 : undefined,
          },
        ]),
  ];
  const known = steps.filter((s) => s.done !== undefined);
  const done = known.filter((s) => s.done).length;
  // Someone who is not an administrator cannot see the steps (the API refuses them): they get the summary.
  const notAdmin = !policies.loading && !policies.data;
  const setUp = notAdmin || (known.length === steps.length && done === steps.length);
  const online = devices.data?.filter((d) => d.online).length;
  const waitingAgents = live.data?.waiting ?? 0;
  const needsYou = (inbox.data?.unread ?? 0) + (pending.data?.length ?? 0);

  if (setUp) {
    return (
      <Page>
        <PageHeader tone={tone} icon={<House />} title="Home" subtitle="Your day: what needs you, what is running and what changed, across the organisation.">
          <HomeClock />
        </PageHeader>
        <PartBoundary key="home" name="Home" tone={tone}>
          <HomeView />
          <LiveSessionsSection tone={tone} title="Live now" hint="Your sessions open to the web and the phone; pick one to watch or carry on." rows={myLive.data?.sessions} />
          <AdoptionPanel />
        </PartBoundary>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<House />}
        title="Get started"
        subtitle={`A few steps, once, and ${discovery?.organisation ?? 'the organisation'} is ready for its workbenches.`}
      />
      <PartBoundary key="home" name="Home" tone={tone}>
      <div className="art-stats">
        <Stat tone={tone} icon={<CircleCheck />} label="Set up" value={known.length ? `${done} of ${steps.length}` : undefined} detail={done === steps.length ? 'Every step done' : `${steps.length - done} to go`} />
        <Stat tone={tone} icon={<Display />} label="Workbenches" value={devices.data?.length} detail={online !== undefined ? `${online} online` : ''} onPress={() => navigate('/workbenches')} />
        <Stat tone={tone} icon={<Pulse />} label="Agents running" value={live.data?.working} detail={waitingAgents ? `${waitingAgents} waiting on a person` : 'None waiting on a person'} onPress={() => navigate('/activity')} />
        <Stat tone={tone} icon={<Bell />} label="Needs you" value={inbox.data || pending.data ? needsYou : undefined} detail={pending.data?.length ? `${pending.data.length} ${pending.data.length === 1 ? 'node waits' : 'nodes wait'} for approval` : 'Unread in your inbox'} needsYou={needsYou > 0} onPress={() => navigate(pending.data?.length ? '/nodes' : '/inbox')} />
      </div>
      <section className="list-group-section" aria-label="Set up">
        <SectionHeading title="Set up" description="Each step goes to the place that does it." />
        <div className="grid gap-2.5 @[40rem]:grid-cols-2">
          {steps.map((s) => (
            <ArtSuggestion key={s.key} tone={s.done ? 'green' : tone} icon={s.done ? <CircleCheck /> : s.icon} onPress={() => navigate(s.to)}>
              <span className="block">{s.title}</span>
              <span className="block text-xs font-normal text-muted">{s.why}</span>
              <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-normal text-muted">
                <span className="status-dot" data-state={s.done === undefined ? 'disabled' : s.done ? 'connected' : 'idle'} aria-hidden="true" />
                {s.done === undefined ? 'Checking' : s.done ? 'Done' : 'To do'}
              </span>
            </ArtSuggestion>
          ))}
        </div>
      </section>
      <LiveSessionsSection tone={tone} title="Live now" hint="Your sessions open to the web and the phone; pick one to watch or carry on." rows={myLive.data?.sessions} />
      <p className="m-0 text-xs text-muted">Once every step is done, Home becomes your day: what needs you, what is running and what finished, across the organisation.</p>
      </PartBoundary>
    </Page>
  );
}
